/**
 * Linking an organization credential into a base.
 *
 * The base half of the connection link the `credential_link` table already models: the
 * org credential stays the single place a secret is entered and rotated, and a base gets
 * a credential row that mirrors it (`managed_by: 'org-link'`). The link is created and
 * removed only through these routes — the data product refuses to mint or drop an
 * org-link row for anyone but this flow.
 *
 * Which credential TYPES may be linked is a v1 bound, not a permanent one: a base
 * consumer reads ONE value, so `api_key` and `bearer` project cleanly and `basic_auth`
 * (two fields) does not. The refusal names the type rather than failing at the far side.
 */

import { z } from 'zod';

/**
 * How a base credential got there: created directly on the base, or projected from a WayAI
 * organization credential. It decides which verbs a row offers on every surface — the web
 * tab, the CLI, and the backend's own delete/rotate guards — so the union is declared once
 * here rather than re-spelled as a bare literal in each of them.
 */
export const BASE_CREDENTIAL_MANAGED_BY = {
  DIRECT: 'direct',
  ORG_LINK: 'org-link',
} as const;

export type BaseCredentialManagedBy =
  (typeof BASE_CREDENTIAL_MANAGED_BY)[keyof typeof BASE_CREDENTIAL_MANAGED_BY];

/**
 * Credential name on the base — wire identity, since it is what a `credential:<name>`
 * reference resolves. Bounded and charset-restricted to match the data product's own
 * validator, so an unusable name is refused here rather than after the bind gate has
 * already decrypted a secret.
 */
export const BASE_CREDENTIAL_NAME_MAX = 100;
export const baseCredentialNameSchema = z
  .string()
  .min(1)
  .max(BASE_CREDENTIAL_NAME_MAX)
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9._:@+~=-]*$/,
    "name must use letters, digits and '. _ : @ + ~ = -', starting with a letter or digit",
  );

export const createBaseCredentialLinkBody = z.object({
  organization_id: z.string().uuid(),
  base_id: z.string().min(1),
  /** Defaults to a slug derived from the credential's display name. */
  name: baseCredentialNameSchema.optional(),
});

export const baseCredentialLinkParams = z.object({
  id: z.string().uuid(),
  base_credential_id: z.string().min(1),
});

export const baseCredentialLinkQuery = z.object({
  organization_id: z.string().uuid(),
  base_id: z.string().min(1),
});

/** What a link looks like from WayAI's side — the base row's id plus where it lives. */
export interface BaseCredentialLinkResponse {
  organization_credential_id: string;
  base_id: string;
  base_credential_id: string;
  name: string;
}

export interface CreateBaseCredentialLinkResponse {
  data: BaseCredentialLinkResponse;
}

/**
 * The post-promote relink outcome, returned ALONGSIDE the promotion result.
 *
 * Org-linked credentials are never published by a promotion (that would orphan WayAI's
 * rotation bookkeeping), so they are re-linked here afterwards. A credential scoped to
 * `preview` cannot bind to a production base, and that is a correct refusal rather than a
 * failure — it is reported by name so the operator sees which credentials production
 * still lacks and why.
 */
export interface PromoteCredentialRelinkResult {
  relinked: string[];
  skipped: { name: string; reason: string }[];
}
